import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { Deployment } from '@/models/deployment.ts'
import { updateDeploymentPreservingConfig } from '@/pages/auto-deploy/updateDeployment'
import { SliderInput } from '@/pages/instance-list/components/SliderInput'

interface EditTtlModalProps {
  open: boolean
  onClose: () => void
  deployment: Deployment
  onUpdate?: () => void
}

export default function EditTtlModal({ open, onClose, deployment, onUpdate }: EditTtlModalProps) {
  const { t } = useTranslation('autoDeploy')
  const [ttlValue, setTtlValue] = useState(() => deployment.ttlSeconds / 60) // in minutes
  const [updating, setUpdating] = useState(false)

  // Initialize modal values when opening
  useEffect(() => {
    if (open) {
      setTtlValue((deployment.ttlSeconds || 0) / 60)
    }
  }, [open, deployment.ttlSeconds])

  // Handle TTL update
  const handleUpdate = useCallback(() => {
    setUpdating(true)
    updateDeploymentPreservingConfig(API, deployment.resID, {
      ttl_seconds: ttlValue * 60,
    })
      .then((res) => {
        if (res.code === 0) {
          message.success(t('common.updateSuccess'))
          onClose()
          onUpdate?.()
        }
      })
      .catch((error) => {
        console.error(error)
        message.error(t('common.updateFailed'))
      })
      .finally(() => {
        setUpdating(false)
      })
  }, [deployment, ttlValue, t, onClose, onUpdate])

  return (
    <Modal
      open={open}
      onClose={updating ? undefined : onClose}
      className="w-[640px] max-w-[calc(100vw-32px)]"
    >
      <Modal.Header>
        <Trans i18nKey="deployTarget.editTtl.title" ns="autoDeploy">
          Edit Instance Idle Retention Period
        </Trans>
      </Modal.Header>
      <Modal.Body className="min-h-[116px]">
        <div className="t-sb mb-2 text-primary">
          <Trans i18nKey="createSubPage.form.ttlSeconds" ns="autoDeploy">
            Instance Idle Retention Period
          </Trans>
        </div>
        <SliderInput
          value={ttlValue}
          min={3}
          max={90}
          unit={t('createSubPage.form.ttlSecondsUnit')}
          step={1}
          onChange={setTtlValue}
          showReset={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]" disabled={updating}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={handleUpdate}
          className="min-w-[88px]"
          disabled={updating || !Number.isInteger(ttlValue) || ttlValue < 3 || ttlValue > 90}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
